import React, { useEffect, useState } from 'react';
import { Typography, useTheme, Button } from '@material-ui/core';
import { MdWarning, MdUpdate, MdInfoOutline, MdMap } from 'react-icons/md';
import { FaTrashAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import formatDate from '../../../utils/formatDate';
import { IOrderDetailed } from '../../../types/orders';
import { useStyles } from './styles';

interface IOrderDetailsPopup {
  order: IOrderDetailed;
  isDeleting?: any;
  onCancelDelete?: any;
  onConfirmDelete?: () => void;
}

const OrderDetailsPopup: React.FC<IOrderDetailsPopup> = ({
  order,
  isDeleting,
  onCancelDelete,
  onConfirmDelete,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [iconColor, setIconColor] = useState<string | any>('#000000');
  const [orderPriorityLabel, setOrderPriorityLabel] = useState(order.priority);
  const [orderStatusLabel, setOrderStatusLabel] = useState(order.status);
  const [isDeletingOrder, setIsDeletingOrder] = useState(false);

  const toggleDelete = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): void => {
    e.preventDefault();
    setIsDeletingOrder(!isDeletingOrder);
  };

  useEffect(() => {
    if (order.priority === 'URGENTE') {
      setIconColor(theme.charts.urgent);
      setOrderPriorityLabel(t('components:utils.urgent'));
    } else if (order.priority === 'ALTA') {
      setIconColor(theme.charts.high);
      setOrderPriorityLabel(t('components:utils.high'));
    } else if (order.priority === 'MEDIA') {
      setIconColor(theme.charts.average);
      setOrderPriorityLabel(t('components:utils.average'));
    } else if (order.priority === 'BAIXA') {
      setIconColor(theme.charts.low);
      setOrderPriorityLabel(t('components:utils.low'));
    } else {
      setIconColor('#757575');
    }
  }, [order, t, theme.charts]);

  useEffect(() => {
    if (order.status === 'ABERTA') {
      setOrderStatusLabel(t('components:utils.open'));
    } else if (order.status === 'EM_EXECUCAO') {
      setOrderStatusLabel(t('components:utils.inProgress'));
    } else if (order.status === 'FINALIZADA') {
      setOrderStatusLabel(t('components:utils.finished'));
    } else if (order.status === 'INCOMPLETA') {
      setOrderStatusLabel(t('components:utils.incomplete'));
    } else {
      setOrderStatusLabel(order.status);
    }
  }, [order, t]);

  useEffect(() => {
    isDeleting(isDeletingOrder);
  }, [isDeletingOrder, isDeleting]);

  return (
    <div className={classes.container}>
      {isDeletingOrder ? (
        <>
          <div className={classes.textContainer}>
            <Typography variant="body1" className={classes.confirmText}>
              {t('screens:orders.confirmDeleteOrder')}
            </Typography>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
              onClick={
                onCancelDelete
                  ? () => onCancelDelete()
                  : () => setIsDeletingOrder(false)
              }
            >
              {t('components:buttons.cancel')}
            </Button>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={onConfirmDelete}
            >
              {t('components:buttons.confirm')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={classes.itemContainer}>
            <div className={classes.iconContainer}>
              <MdWarning size={24} color={iconColor} />
            </div>
            <div className={classes.labelContainer}>
              <Typography
                variant="subtitle1"
                className={classes.label}
                style={{ color: iconColor }}
              >
                {orderPriorityLabel.toUpperCase()}
              </Typography>
            </div>
          </div>
          <div className={classes.itemContainer}>
            <div className={classes.iconContainer}>
              <MdUpdate size={24} color="#757575" />
            </div>
            <div className={classes.labelContainer}>
              <Typography variant="subtitle1" className={classes.label}>
                {formatDate(new Date(order.timestamp))}
              </Typography>
            </div>
          </div>
          <div className={classes.itemContainer}>
            <div className={classes.iconContainer}>
              <MdInfoOutline size={24} color="#757575" />
            </div>
            <div className={classes.labelContainer}>
              <Typography variant="subtitle1" className={classes.label}>
                {orderStatusLabel.toUpperCase()}
              </Typography>
            </div>
          </div>
          <div className={classes.itemContainer}>
            <div className={classes.iconContainer}>
              <MdMap size={24} color="#757575" />
            </div>
            <div className={classes.labelContainer}>
              <Typography variant="subtitle1" className={classes.label}>
                {order.region}
              </Typography>
            </div>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<FaTrashAlt size={20} />}
              className={classes.button}
              onClick={e => toggleDelete(e)}
            >
              {t('components:buttons.deleteOrder')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderDetailsPopup;
